/**
 * TrajectorySparkline · Jul 2026
 * ================================================================
 * A compact SVG line chart that renders the last N Operating-Health
 * snapshots stored on `business.operating_health_history`. Designed
 * to sit inline next to any Operating Health pill / chip / hero so
 * founders can see the *trend* in the same glance as the *score*.
 *
 * Pure presentational component — takes the history array (or the
 * whole business doc), everything else derives from that.
 *
 * Design tokens (Three Little Birds):
 *   • Primary #7C5FB0 · sharp edges · uppercase deltas
 *   • Positive delta emerald-600 · Negative red-600
 */
import { useMemo } from "react";

// Defaults chosen to look right next to a 44px x-height chip.
const DEFAULT_WIDTH = 56;
const DEFAULT_HEIGHT = 24;
const PAD = 2;

export function pickRecentSnapshots(history, limit = 4) {
  if (!Array.isArray(history) || history.length === 0) return [];
  // History is time-ascending. We want the last `limit` weeks —
  // approximate by taking the last `limit` snapshots. If the founder
  // logs multiple in a week the trend still reads correctly because
  // the sparkline only shows relative movement.
  return history.slice(-limit).map((h) => ({
    date: h?.date || "",
    pct: Math.max(0, Math.min(100, Number(h?.pct) || 0)),
  }));
}

export function computeDelta(points) {
  if (!points || points.length < 2) return 0;
  const first = points[0].pct;
  const last = points[points.length - 1].pct;
  return last - first;
}

function buildPath(points, w, h) {
  if (points.length === 0) return "";
  const innerW = w - PAD * 2;
  const innerH = h - PAD * 2;
  const step = points.length > 1 ? innerW / (points.length - 1) : 0;
  return points
    .map((p, i) => {
      const x = PAD + i * step;
      const y = PAD + innerH - (p.pct / 100) * innerH;
      return `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
}

export default function TrajectorySparkline({
  history,
  business,
  limit = 4,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  showDelta = true,
  className = "",
  testid = "trajectory-sparkline",
}) {
  const hist = useMemo(
    () => history ?? business?.operating_health_history ?? [],
    [history, business?.operating_health_history],
  );
  const points = useMemo(() => pickRecentSnapshots(hist, limit), [hist, limit]);
  const delta = useMemo(() => computeDelta(points), [points]);

  // Empty state — a founder who just graduated has 1 point (the
  // day-0 seed). Rendering nothing would be confusing; render a
  // dashed baseline to hint "not enough data yet".
  if (points.length === 0) {
    return (
      <div
        className={`inline-flex items-center gap-2 ${className}`}
        data-testid={`${testid}-empty`}
        aria-label="No trajectory data yet"
      >
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden>
          <line
            x1={PAD}
            y1={height / 2}
            x2={width - PAD}
            y2={height / 2}
            stroke="#D4D4D8"
            strokeWidth="1.5"
            strokeDasharray="2 3"
          />
        </svg>
      </div>
    );
  }

  const path = buildPath(points, width, height);
  const last = points[points.length - 1];
  const lastX = PAD + (points.length > 1 ? (points.length - 1) * ((width - PAD * 2) / (points.length - 1)) : 0);
  const lastY = PAD + (height - PAD * 2) - (last.pct / 100) * (height - PAD * 2);

  const deltaLabel =
    delta === 0 ? "±0" : delta > 0 ? `+${Math.round(delta)}` : `${Math.round(delta)}`;
  const deltaClass =
    delta > 0
      ? "text-emerald-600"
      : delta < 0
      ? "text-red-600"
      : "text-neutral-500";

  return (
    <div
      className={`inline-flex items-center gap-2 ${className}`}
      data-testid={testid}
      title={`Last ${points.length} Operating-Health snapshots · ${points
        .map((p) => `${p.date}: ${p.pct}%`)
        .join(" · ")}`}
    >
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        aria-hidden
        data-testid={`${testid}-svg`}
      >
        <path
          d={path}
          fill="none"
          stroke="#7C5FB0"
          strokeWidth="1.5"
          strokeLinecap="square"
          strokeLinejoin="miter"
        />
        <rect
          x={lastX - 2}
          y={lastY - 2}
          width="4"
          height="4"
          fill="#7C5FB0"
          stroke="#4A4458"
          strokeWidth="0.75"
        />
      </svg>
      {showDelta && points.length >= 2 && (
        <span
          className={`text-[10px] font-black uppercase tracking-[0.16em] ${deltaClass}`}
          data-testid={`${testid}-delta`}
        >
          {deltaLabel}
        </span>
      )}
    </div>
  );
}
